import type { ReactElement } from "react";
import { useMemo } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import type {
    CustomBasicModalProps,
} from "app/pages/sales-intelligence/common-components/modals/CustomBasicModal/CustomBasicModal";
import CustomBasicModal from "app/pages/sales-intelligence/common-components/modals/CustomBasicModal/CustomBasicModal";
import type { Translatable } from "app/pages/sales-intelligence/types";
import { AssetsService } from "app/services/AssetsService";

import {
    ONBOARDING_INFO_MODAL_STYLES,
    StyledOnboardingInfoImage,
    StyledOnboardingInfoList,
    StyledOnboardingInfoPopupContentWrapper,
    StyledOnboardingInfoPopupFooter,
    StyledOnboardingInfoPopupHeader,
} from "./styles";

export interface IOnboardingInfoModalProps extends Pick<CustomBasicModalProps, "isOpen" | "onClose"> {
    title: string;
    description?: string;
    descriptionList?: readonly Translatable[];
    actionLabel: string;
    imageSrc?: string;
    onActionClick: VoidFunction;
}

const OnboardingInfoModal = ({
    title,
    description,
    descriptionList,
    imageSrc,
    actionLabel,
    isOpen,
    onClose,
    onActionClick,
}: IOnboardingInfoModalProps): ReactElement => {
    const imageElement = imageSrc ? (
        <Flex justifyContent="center">
            <StyledOnboardingInfoImage
                src={AssetsService.assetUrl(imageSrc)}
                alt="info-image"
                data-automation="info-modal.image"
            />
        </Flex>
    ) : null;

    const descriptionElement = description ? (
        <Text
            fontVariant="body1"
            color={colorsPalettes.carbon[400]}
            data-automation="info-modal.description"
        >
            {description}
        </Text>
    ) : null;

    const descriptionListElement = useMemo(() => {
        if (!descriptionList?.length) {
            return null;
        }

        return (
            <Text
                fontVariant="body1"
                color={colorsPalettes.carbon[400]}
                data-automation="info-modal.description-list"
            >
                <StyledOnboardingInfoList>
                    {descriptionList.map((descriptionListItem) => (
                        <li data-automation="info-modal.description-list-item">
                            {translate(descriptionListItem.key, descriptionListItem.replacements)}
                        </li>
                    ))}
                </StyledOnboardingInfoList>
            </Text>
        );
    }, [descriptionList]);

    return (
        <CustomBasicModal
            isOpen={isOpen}
            onClose={onClose}
            customStyles={ONBOARDING_INFO_MODAL_STYLES}
            shouldCloseOnOverlayClick={false}
            withCloseIcon={!!onClose}
        >
            <StyledOnboardingInfoPopupHeader data-automation="info-modal.header">
                <Text
                    fontVariant="h5"
                    color={colorsPalettes.carbon[500]}
                >
                    {title}
                </Text>
            </StyledOnboardingInfoPopupHeader>

            <StyledOnboardingInfoPopupContentWrapper
                flexDirection="column"
                gap="32px"
                data-automation="info-modal.content"
            >
                {imageElement}

                <Flex
                    flexDirection="column"
                    gap="20px"
                >
                    {descriptionElement}
                    {descriptionListElement}
                </Flex>
            </StyledOnboardingInfoPopupContentWrapper>

            <StyledOnboardingInfoPopupFooter
                justifyContent="flex-end"
                gap="16px"
                data-automation="info-modal.footer"
            >
                <Button onClick={onActionClick}>{actionLabel}</Button>
            </StyledOnboardingInfoPopupFooter>
        </CustomBasicModal>
    );
};

export default OnboardingInfoModal;
